import React, { useState, useEffect, useRef } from 'react';
import { Send, Sparkles, RefreshCw, Loader2, MessageCircle, X } from 'lucide-react';
import BASE_URL from './config';

/**
 * Ride group chat component with persistent history and AI join summary.
 *
 * Shows message history, allows sending new messages, and provides
 * an optional AI-generated summary for new joiners.
 */
function RideChat({ rideId, currentUser, isOpen, onClose }) {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  // AI summary state
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Fetch messages when chat opens
  useEffect(() => {
    if (isOpen && rideId) {
      fetchMessages();
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [isOpen, rideId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/rides/${rideId}/messages`, {
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          setError('You are not a participant of this ride.');
          return;
        }
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      setMessages(data.messages || []);
      setError(null);
      // Mark notifications as read now that messages are visible
      fetch(`${BASE_URL}/api/notifications/mark-read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ rideId, type: 'message' }),
      }).catch(() => {});
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    const content = newMessage.trim();
    if (!content || sending) return;

    setSending(true);
    try {
      const response = await fetch(`${BASE_URL}/api/rides/${rideId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content }),
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to send message');
      }

      const data = await response.json();
      setMessages(prev => [...prev, data.message]);
      setNewMessage('');
      inputRef.current?.focus();
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  const fetchSummary = async (force = false) => {
    setSummaryLoading(true);
    try {
      const url = `${BASE_URL}/api/rides/${rideId}/summary${force ? '?force=true' : ''}`;
      const response = await fetch(url, { credentials: 'include' });

      if (!response.ok) throw new Error('Failed to generate summary');

      const data = await response.json();
      setSummary(data);
      setShowSummary(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      const now = new Date();
      const isToday = date.toDateString() === now.toDateString();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const isYesterday = date.toDateString() === yesterday.toDateString();

      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

      if (isToday) return timeStr;
      if (isYesterday) return `Yesterday ${timeStr}`;
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + timeStr;
    } catch {
      return '';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="ride-chat-overlay" onClick={onClose}>
      <div className="ride-chat-panel" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="ride-chat-header">
          <div className="ride-chat-header-left">
            <MessageCircle className="w-4 h-4" />
            <span>Ride Chat</span>
          </div>
          <div className="ride-chat-header-actions">
            <button
              className="ride-chat-summary-btn"
              onClick={() => summary ? setShowSummary(!showSummary) : fetchSummary()}
              disabled={summaryLoading}
              title="AI Summary of conversation"
            >
              {summaryLoading ? <Loader2 className="w-3 h-3 nl-spin" /> : <Sparkles className="w-3 h-3" />}
              Summary
            </button>
            <button className="ride-chat-close" onClick={onClose}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* AI Summary panel */}
        {showSummary && summary && (
          <div className="ride-chat-summary">
            <div className="ride-chat-summary-header">
              <span className="ride-chat-summary-label">
                <Sparkles className="w-3 h-3" />
                {summary.is_ai_generated ? 'AI-Generated Summary' : 'Chat Overview'}
              </span>
              <button
                className="ride-chat-summary-refresh"
                onClick={() => fetchSummary(true)}
                disabled={summaryLoading}
                title="Regenerate summary"
              >
                <RefreshCw className={`w-3 h-3 ${summaryLoading ? 'nl-spin' : ''}`} />
              </button>
            </div>
            <div className="ride-chat-summary-content">
              {summary.summary}
            </div>
            <div className="ride-chat-summary-disclaimer">
              This summary is AI-generated and may not be fully accurate. Always check the actual messages.
              {summary.cached && ' (cached)'}
            </div>
          </div>
        )}

        {/* Messages area */}
        <div className="ride-chat-messages">
          {loading ? (
            <div className="ride-chat-loading">
              <Loader2 className="w-5 h-5 nl-spin" />
              <span>Loading messages...</span>
            </div>
          ) : error ? (
            <div className="ride-chat-error">{error}</div>
          ) : messages.length === 0 ? (
            <div className="ride-chat-empty">
              <MessageCircle className="w-8 h-8" style={{ opacity: 0.3 }} />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg, idx) => {
              const isMe = msg.senderUsername === currentUser;
              return (
                <div
                  key={msg.id || idx}
                  className={`ride-chat-message ${isMe ? 'ride-chat-message-mine' : 'ride-chat-message-other'}`}
                >
                  {!isMe && (
                    <div className="ride-chat-message-sender">
                      {msg.senderName || msg.senderUsername}
                    </div>
                  )}
                  <div className="ride-chat-message-bubble">
                    {msg.content}
                  </div>
                  <div className="ride-chat-message-time">
                    {formatTime(msg.createdAt)}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input area */}
        <div className="ride-chat-input-area">
          <input
            ref={inputRef}
            type="text"
            className="ride-chat-input"
            placeholder="Type a message..."
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
            maxLength={2000}
          />
          <button
            className="ride-chat-send"
            onClick={sendMessage}
            disabled={sending || !newMessage.trim()}
          >
            {sending ? <Loader2 className="w-4 h-4 nl-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export default RideChat;
